import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CreditCard, Receipt } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { api, errMsg } from "@/lib/api";
import { openCheckout } from "@/lib/razorpay";

const fmt = (iso) => (iso ? new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—");

export default function Billing() {
  const [b, setB] = useState(null);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get("/billing").then((r) => setB(r.data)), []);
  useEffect(() => { load(); api.get("/public/join-options").then((r) => setPlans(r.data.plans)); }, [load]);

  const buy = async (p) => {
    setBusy(p.id);
    try {
      const { data: order } = await api.post("/billing/order", { plan_id: p.id });
      const pay = await openCheckout(order, { name: "PVC Card Studio", description: `${p.name} plan` });
      await api.post("/billing/verify", { ref: order.ref, ...pay });
      toast.success(`${p.name} plan activated`);
      window.location.reload();
    } catch (err) { toast.error(err.response ? errMsg(err) : err.message); } finally { setBusy(""); }
  };

  if (!b) return <div className="min-h-screen bg-slate-950"><AppHeader /><p className="p-10 text-slate-500">Loading…</p></div>;
  const pct = b.card_limit ? Math.min(100, (b.cards_used / b.card_limit) * 100) : 0;

  return (
    <div className="min-h-screen bg-slate-950">
      <AppHeader />
      <main className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-amber-400">Subscription</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight mt-2">Plan & billing</h1>
        </div>
        <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 grid sm:grid-cols-3 gap-6" data-testid="billing-current">
          <div><p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Current plan</p><p className="font-heading text-2xl font-extrabold mt-1" data-testid="billing-plan-name">{b.is_trial ? "Free trial" : b.plan_name || "Custom"}</p></div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Cards used</p>
            <p className="text-2xl font-bold mt-1" data-testid="billing-usage">{b.cards_used}<span className="text-slate-500 text-base"> / {b.card_limit || "∞"}</span></p>
            {b.card_limit && <div className="h-1.5 rounded-full bg-slate-800 mt-2"><div className="h-full rounded-full bg-amber-400" style={{ width: `${pct}%` }} /></div>}
          </div>
          <div><p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Valid till</p><p className="text-2xl font-bold mt-1" data-testid="billing-expiry">{b.is_trial ? "Trial" : fmt(b.expires_at)}</p></div>
          {b.is_trial && <p className="sm:col-span-3 text-xs text-emerald-300" data-testid="billing-trial-note">Trial: {b.card_limit} cards, editing and deleting locked. Subscribe below to unlock everything.</p>}
        </section>

        <section className="space-y-3">
          <p className="font-heading font-bold flex items-center gap-2"><CreditCard size={16} className="text-amber-400" /> {b.is_trial ? "Subscribe" : "Renew or upgrade"}</p>
          {!b.payments_enabled && <p className="text-xs text-orange-300" data-testid="billing-payments-off">Online payment isn't switched on yet. Contact the administrator to renew.</p>}
          <div className="grid sm:grid-cols-3 gap-4">
            {plans.map((p, i) => (
              <div key={p.id} className="relative rounded-xl border border-slate-800 bg-slate-900/60 p-5 overflow-hidden" data-testid={`billing-plan-${i}`}>
                <div className="absolute inset-x-0 top-0 h-1" style={{ background: p.color }} />
                <p className="font-heading text-xl font-extrabold" style={{ color: p.color }}>{p.name}</p>
                <p className="text-3xl font-bold mt-1">₹{p.amount.toLocaleString("en-IN")}</p>
                <p className="text-sm text-slate-400 mt-2">{p.card_limit ? `${p.card_limit.toLocaleString("en-IN")} cards` : "Unlimited cards"} · {p.duration_months} month{p.duration_months > 1 ? "s" : ""}</p>
                <button disabled={!!busy || !b.payments_enabled || p.amount < 1} onClick={() => buy(p)} className="cf-btn-primary w-full h-10 mt-4" data-testid={`billing-buy-${i}`}>
                  {busy === p.id && <Loader2 size={14} className="animate-spin" />} Pay ₹{p.amount.toLocaleString("en-IN")}
                </button>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-500">Paying adds the plan's months to your current expiry date (or starts today).</p>
        </section>

        <section className="rounded-xl border border-slate-800 bg-slate-900/60" data-testid="billing-history">
          <p className="font-heading font-bold px-5 py-4 border-b border-slate-800 flex items-center gap-2"><Receipt size={15} className="text-amber-400" /> Payment history</p>
          {!b.payments.length ? <p className="p-6 text-sm text-slate-500">No payments yet.</p> : (
            <table className="w-full text-xs">
              <tbody>{b.payments.map((p, i) => (
                <tr key={p.id} className="border-b border-slate-800/60" data-testid={`billing-payment-${i}`}>
                  <td className="px-5 py-3">{fmt(p.created_at)}</td><td className="px-4 py-3 font-semibold">{p.plan_name}</td>
                  <td className="px-4 py-3 font-mono">₹{p.amount.toLocaleString("en-IN")}</td><td className="px-4 py-3 font-mono text-slate-500">{p.payment_id}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </section>
      </main>
    </div>
  );
}
